import { useEffect, useReducer, useState } from "react";
import { Button } from "../../../components/ui/Button";
import { PicoBubble } from "../../../components/PicoBubble";
import { useI18n } from "../../../i18n";
import { useSessionManager } from "../../../store/session/SessionProvider";
import type { RoomConnection } from "../lib/connection";
import { TICK_MS } from "../lib/connection";
import type { RoomCatalog, RoomEvent, RoomMeta } from "../types";
import { sumoFromMeta, sumoReduce, type CarryDone, type SumoState } from "../lib/modeState";

/**
 * P2-S3 룸 모드 화면 조각 — FS-05 밀어내기(대기 · 대전 · 결과는 나만) · FS-06 협동(팀 · 목표까지 · 팀 완료) · 이모트 바(SR-SOC-01).
 * 점수·순위·승수 없음(DR-01) · 자유 입력 없음(DR-02 — 카탈로그 버튼만).
 */

const EMPTY_CATALOG: RoomCatalog = { emotes: [], phrases: [], hints: new Map() };

const items = (v: unknown): Record<string, unknown>[] =>
  (Array.isArray(v)
    ? v
    : Array.isArray((v as { items?: unknown })?.items)
      ? (v as { items: unknown[] }).items
      : []) as Record<string, unknown>[];

/** 카탈로그(GET /api/catalog?type=emote|phrase|hint_rule) — 없으면 빈 바 */
export function useRoomCatalog(): RoomCatalog {
  const manager = useSessionManager();
  const [cat, setCat] = useState<RoomCatalog>(EMPTY_CATALOG);
  useEffect(() => {
    let alive = true;
    const get = (type: string) =>
      manager
        .request<unknown>(`/api/catalog?type=${type}`)
        .then(items, () => [] as Record<string, unknown>[]);
    void Promise.all([get("emote"), get("phrase"), get("hint_rule")]).then(([e, p, h]) => {
      if (!alive) return;
      const on = (o: Record<string, unknown>) => o.enabled !== false;
      const payload = (o: Record<string, unknown>) => (o.payload ?? {}) as Record<string, unknown>;
      setCat({
        emotes: e
          .filter(on)
          .map((o) => ({ key: String(o.key), emoji: String(payload(o).emoji ?? "") }))
          .filter((x) => x.key && x.emoji),
        phrases: p
          .filter(on)
          .map((o) => ({
            key: String(o.key),
            text:
              (payload(o).text as { ko?: string; en?: string }) ??
              (o.i18n as { ko?: string; en?: string }) ??
              {},
          }))
          .filter((x) => x.key),
        hints: new Map(
          h
            .filter(on)
            .map(
              (o) =>
                [
                  String(payload(o).pattern ?? ""),
                  (o.i18n ?? {}) as { ko?: string; en?: string },
                ] as const,
            )
            .filter(([k]) => k),
        ),
      });
    });
    return () => {
      alive = false;
    };
  }, [manager]);
  return cat;
}

/** 이모트 말풍선 — 기계 번호 → 표시(2초) */
export function useEmoteBubbles(
  conn: RoomConnection | null,
  catalog: RoomCatalog,
  now: () => number,
) {
  const [bubbles, setBubbles] = useState(new Map<number, { text: string; until: number }>());
  const [, tick] = useReducer((x: number) => x + 1, 0);
  useEffect(() => {
    if (!conn) return;
    return conn.onEvent((e) => {
      if (e.t !== "emote" || typeof e.machine !== "number") return;
      const key = String(e.key);
      const emoji = catalog.emotes.find((x) => x.key === key)?.emoji;
      const phrase = catalog.phrases.find((x) => x.key === key)?.text;
      const text = emoji ?? phrase?.ko ?? phrase?.en ?? key;
      setBubbles((b) => new Map(b).set(e.machine as number, { text, until: now() + 2000 }));
      window.setTimeout(tick, 2050);
    });
  }, [conn, catalog, now]);
  const t = now();
  return new Map([...bubbles].filter(([, v]) => v.until > t).map(([k, v]) => [k, v.text]));
}

// ── 밀어내기 ────────────────────────────────────────────────────────────────

export function SumoPanel({
  conn,
  meta,
  catalog,
}: {
  conn: RoomConnection;
  meta: RoomMeta;
  catalog: RoomCatalog;
}) {
  const { t, locale } = useI18n();
  // 연결이 이미 받은 모드 이벤트에서 시작한다(구독 전에 온 match 를 잃지 않게 — lib/modeState.ts)
  const initial = () => conn.modeState.sumo ?? sumoFromMeta(meta.mode);
  const [st, setSt] = useState<SumoState>(initial);
  const [, rerender] = useReducer((x: number) => x + 1, 0);
  // eslint-disable-next-line react-hooks/exhaustive-deps -- 새 FullState(meta) 때만 다시(연결 상태는 그때 비었다)
  useEffect(() => setSt(initial()), [meta]);
  useEffect(() => conn.onEvent((e: RoomEvent) => setSt((s) => sumoReduce(s, e))), [conn]);
  useEffect(() => {
    if (st.phase !== "match" && st.phase !== "paused" && st.phase !== "countdown") return;
    const h = window.setInterval(rerender, st.phase === "countdown" ? 200 : 500);
    return () => window.clearInterval(h);
  }, [st.phase]);
  useEffect(() => {
    if (st.phase !== "ended") return;
    const h = window.setTimeout(() => setSt({ phase: "wait", next: false }), 2500);
    return () => window.clearTimeout(h);
  }, [st.phase]);

  // 끊김 멈춤(2026-09-29) — 상대가 3초 넘게 끊겼다 · 20초 안에 안 오면 무효
  if (st.phase === "paused") {
    const left = Math.max(
      0,
      Math.ceil(((st.untilTick - (conn.serverTick() ?? st.untilTick)) * TICK_MS) / 1000),
    );
    return (
      <div className="pf-room__mode" data-testid="fs-05-02" data-phase="paused">
        <p className="pf-room__vs">{t("room.sumo.vs", { name: st.vs })}</p>
        <div role="status" data-testid="sumo-paused" data-left={left}>
          <p>{t("room.sumo.paused")}</p>
          <b className="pf-room__timer">{left}</b>
          <p className="pf-muted">{t("room.sumo.pausedNote", { s: left })}</p>
        </div>
      </div>
    );
  }
  // 모두 돌아왔다 — 3·2·1(입력 무시) 뒤 그 자리에서 이어서. goTick 이 지나면 경기로 그린다(곧 match 이벤트)
  const now = conn.serverTick();
  if (st.phase === "countdown" && (now === null || now < st.goTick)) {
    const n = Math.max(1, Math.ceil(((st.goTick - (now ?? 0)) * TICK_MS) / 1000));
    return (
      <div className="pf-room__mode" data-testid="fs-05-02" data-phase="countdown">
        <p className="pf-room__vs">{t("room.sumo.vs", { name: st.vs })}</p>
        <div role="status" data-testid="sumo-countdown" data-count={n}>
          <b className="pf-room__stamp">{n}</b>
          <p className="pf-muted">{t("room.sumo.resume")}</p>
        </div>
      </div>
    );
  }
  if (st.phase === "match" || st.phase === "countdown") {
    const left = Math.max(0, ((st.endsTick - (now ?? st.endsTick)) * TICK_MS) / 1000);
    const mmss = `${Math.floor(left / 60)}:${String(Math.floor(left % 60)).padStart(2, "0")}`;
    return (
      <div className="pf-room__mode" data-testid="fs-05-02">
        <p className="pf-room__vs">{t("room.sumo.vs", { name: st.vs })}</p>
        <b className="pf-room__timer" data-testid="sumo-timer">
          {mmss}
        </b>
        <p className="pf-muted">{t("room.sumo.rule")}</p>
      </div>
    );
  }
  if (st.phase === "result") {
    const hint =
      st.outcome === "lose" && st.hint
        ? (catalog.hints.get(st.hint)?.[locale as "ko" | "en"] ?? t(`room.hint.${st.hint}`))
        : null;
    return (
      <div className="pf-room__mode" data-testid="fs-05-03" data-outcome={st.outcome}>
        <b className="pf-room__stamp">{t(`room.sumo.${st.outcome}`)}</b>
        <p className="pf-muted">
          {t(st.outcome === "void" ? "room.sumo.voidNote" : "room.sumo.onlyUs")}
        </p>
        {hint && <PicoBubble text={hint} />}
        <Button
          variant="green"
          onClick={() => setSt({ phase: "wait", next: false })}
          data-testid="sumo-next"
        >
          {t("room.sumo.nextMatch")}
        </Button>
      </div>
    );
  }
  return (
    <div className="pf-room__mode" data-testid="fs-05-01">
      <p>
        {st.phase === "ended"
          ? t("room.sumo.matchEnded")
          : t(st.next ? "room.sumo.next" : "room.sumo.waiting")}
      </p>
      <p className="pf-muted">{t("room.sumo.watching")}</p>
    </div>
  );
}

// ── 협동 ────────────────────────────────────────────────────────────────

interface Lane {
  index: number;
  cargo: { key: string; titleKey?: string; pieceId: string };
  start: [number, number, number];
  goal: { center: [number, number, number] };
  members: { machine: number; name: string }[];
}

export function CarryPanel({
  conn,
  meta,
  catalog,
}: {
  conn: RoomConnection;
  meta: RoomMeta;
  catalog: RoomCatalog;
}) {
  const { t, locale } = useI18n();
  const team = typeof meta.mode.team === "number" ? (meta.mode.team as number) : null;
  const lanes = (meta.mode.lanes ?? []) as Lane[];
  const lane = team !== null ? lanes[team] : null;
  const endsTick = Number(meta.mode.endsTick ?? 0);
  // 연결이 이미 받은 팀 결과에서 시작한다(구독 전에 온 teamComplete · teamEnd — lib/modeState.ts)
  const [done, setDone] = useState<CarryDone | null>(() => conn.modeState.carryDone);
  const [, rerender] = useReducer((x: number) => x + 1, 0);
  // eslint-disable-next-line react-hooks/exhaustive-deps -- 새 FullState(meta) 때만
  useEffect(() => setDone(conn.modeState.carryDone), [meta]);
  useEffect(
    () =>
      conn.onEvent((e) => {
        if (e.t === "teamComplete") setDone({ outcome: "complete", hint: null });
        else if (e.t === "teamEnd")
          setDone({ outcome: "incomplete", hint: typeof e.hint === "string" ? e.hint : null });
      }),
    [conn],
  );
  useEffect(() => {
    const h = window.setInterval(rerender, 500);
    return () => window.clearInterval(h);
  }, []);
  // 목표까지 — 물체가 처음 자리에서 목표 쪽으로 간 비율(거리 게이지 · 비교 없음 · 0.5초마다 다시)
  const progress = (() => {
    if (!lane) return 0;
    const f = conn.frame();
    const idx = meta.bodies.filter((b) => b.kind === "object")[lane.index]?.index;
    if (!f || idx === undefined) return 0;
    const z = f.transforms[idx * 7 + 2]!;
    const span = lane.goal.center[2] - lane.start[2];
    return Math.max(0, Math.min(1, (z - lane.start[2]) / (span || 1)));
  })();
  if (!lane)
    return (
      <div className="pf-room__mode" data-testid="fs-06-01" data-team="none">
        <p>{t("room.carry.watching")}</p>
      </div>
    );
  const left = Math.max(0, ((endsTick - (conn.serverTick() ?? endsTick)) * TICK_MS) / 1000);
  const mmss = `${Math.floor(left / 60)}:${String(Math.floor(left % 60)).padStart(2, "0")}`;
  if (done) {
    const hint =
      done.outcome === "incomplete" && done.hint
        ? (catalog.hints.get(done.hint)?.[locale as "ko" | "en"] ?? t(`room.hint.${done.hint}`))
        : null;
    return (
      <div className="pf-room__mode" data-testid="fs-06-02" data-outcome={done.outcome}>
        <b className="pf-room__stamp">
          {t(done.outcome === "complete" ? "room.carry.done" : "room.carry.again")}
        </b>
        {done.outcome === "complete" && <p>{t("room.carry.badge")}</p>}
        {hint && <PicoBubble text={hint} />}
      </div>
    );
  }
  return (
    <div className="pf-room__mode" data-testid="fs-06-01">
      <p>{t("room.carry.team", { names: lane.members.map((m) => m.name).join(" · ") })}</p>
      <p className="pf-muted">{t(`room.carry.cargo.${lane.cargo.key}`)}</p>
      <div
        className="pf-room__gauge"
        aria-label={t("room.carry.toGoal")}
        data-testid="carry-progress"
        data-progress={progress.toFixed(2)}
      >
        <span style={{ width: `${Math.round(progress * 100)}%` }} />
      </div>
      <b className="pf-room__timer">{mmss}</b>
    </div>
  );
}

// ── 이모트 바 ───────────────────────────────────────────────────────────

export function EmoteBar({
  conn,
  catalog,
  phrases,
}: {
  conn: RoomConnection;
  catalog: RoomCatalog;
  phrases: boolean;
}) {
  const { t, locale } = useI18n();
  if (!catalog.emotes.length && !catalog.phrases.length) return null;
  return (
    <div
      className="pf-room__emotes"
      role="group"
      aria-label={t("room.emotes")}
      data-testid="emote-bar"
    >
      {catalog.emotes.slice(0, 6).map((e) => (
        <button
          key={e.key}
          type="button"
          className="pf-btn pf-btn--ghost pf-room__emote"
          data-emote={e.key}
          onClick={() => conn.sendEmote(e.key)}
        >
          {e.emoji}
        </button>
      ))}
      {phrases &&
        catalog.phrases.slice(0, 3).map((p) => (
          <button
            key={p.key}
            type="button"
            className="pf-btn pf-btn--ghost pf-room__phrase"
            data-emote={p.key}
            onClick={() => conn.sendEmote(p.key)}
          >
            {p.text[locale as "ko" | "en"] ?? p.text.ko ?? p.key}
          </button>
        ))}
    </div>
  );
}
